import { eventBus } from './EventBus';
import { SaveData } from './SaveManager';

export interface GameSettings {
  timeSpeed: number; // 1.0 = 1 real sec = 1 game min
}

export class Game {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private lastTime: number = 0;
  private isRunning: boolean = false;
  private animationFrameId: number = 0;

  // Logic loop: fixed 1 second step
  private lastLogicTime: number = 0;
  private readonly LOGIC_INTERVAL = 1000; // ms

  // Render loop: fixed ~15fps
  private lastRenderTime: number = 0;
  private readonly RENDER_INTERVAL = 66; // ~15fps

  settings: GameSettings = {
    timeSpeed: 5.0 // Default: 5x speed
  };

  // Game state
  gameTime: number = 0; // in game-minutes
  totalPlayTime: number = 0; // in real milliseconds
  private sessionStartTime: number = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.resize();
    let resizeTimer: ReturnType<typeof setTimeout>;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.resize(), 150);
    });
  }

  private resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    this.ctx.scale(dpr, dpr);
  }

  start(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.sessionStartTime = performance.now();
    this.lastTime = performance.now();
    this.lastLogicTime = performance.now();
    this.lastRenderTime = performance.now();
    this.loop();
    eventBus.emit('game:start');
  }

  stop(): void {
    this.isRunning = false;
    cancelAnimationFrame(this.animationFrameId);
    const sessionTime = performance.now() - this.sessionStartTime;
    this.totalPlayTime += sessionTime;
    eventBus.emit('game:stop');
  }

  private loop = (): void => {
    if (!this.isRunning) return;
    const now = performance.now();
    const deltaTime = now - this.lastTime;
    this.lastTime = now;

    // Accumulate game time (continuous, even between logic updates)
    const gameDelta = (deltaTime / 1000) * this.settings.timeSpeed;
    this.gameTime += gameDelta;

    // Logic update: fixed 1-second step
    const logicDelta = now - this.lastLogicTime;
    if (logicDelta >= this.LOGIC_INTERVAL) {
      this.lastLogicTime = now;
      // Pass actual seconds elapsed (capped at 2x interval to prevent huge jumps)
      const logicSeconds = Math.min(logicDelta / 1000, this.LOGIC_INTERVAL * 2 / 1000);
      const gameLogicDelta = logicSeconds * this.settings.timeSpeed;
      eventBus.emit('game:update', gameLogicDelta, this.gameTime);
    }

    // Render: fixed ~15fps
    const renderDelta = now - this.lastRenderTime;
    if (renderDelta >= this.RENDER_INTERVAL) {
      this.lastRenderTime = now;
      this.render();
    }

    this.animationFrameId = requestAnimationFrame(this.loop);
  };

  private render(): void {
    const width = this.canvas.width / (window.devicePixelRatio || 1);
    const height = this.canvas.height / (window.devicePixelRatio || 1);

    this.ctx.clearRect(0, 0, width, height);
    eventBus.emit('game:render', this.ctx, width, height, this.gameTime);
  }

  getCurrentTime(): number {
    return this.gameTime;
  }

  getFormattedTime(): string {
    const totalMinutes = Math.floor(this.gameTime);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  createSaveData(plantsData: any[], currency: number): SaveData {
    const sessionTime = performance.now() - this.sessionStartTime;
    return {
      version: '2.0',
      createdAt: Date.now(),
      lastPlayedAt: Date.now(),
      totalPlayTime: this.totalPlayTime + sessionTime,
      plants: plantsData,
      currency,
      settings: { ...this.settings }
    };
  }

  loadSaveData(data: SaveData): void {
    this.totalPlayTime = data.totalPlayTime || 0;
    this.settings = { ...data.settings };
  }

  save(): void {
    eventBus.emit('game:save');
  }

  exportSave(): void {
    eventBus.emit('game:export');
  }
}
